import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { trpc } from '@/lib/trpc'
import type { HomeworkTask } from '../data/types'

interface BehavioralSubmissionDialogProps {
  task: HomeworkTask
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STAR_FIELDS = [
  { key: 'situation', label: 'Situation' },
  { key: 'task', label: 'Task' },
  { key: 'action', label: 'Action' },
  { key: 'result', label: 'Result' },
] as const

type StarKey = (typeof STAR_FIELDS)[number]['key']

/**
 * Client submission form for behavioral assignments: captures the STAR spans
 * and an optional recording reference, then freezes the submission snapshot.
 */
export function BehavioralSubmissionDialog({
  task,
  open,
  onOpenChange,
}: BehavioralSubmissionDialogProps) {
  const utils = trpc.useUtils()
  const mutation = trpc.dashboard.plan.submitForReview.useMutation()
  const [star, setStar] = useState<Record<StarKey, string>>({
    situation: '',
    task: '',
    action: '',
    result: '',
  })
  const [recordingRef, setRecordingRef] = useState('')
  const [minutes, setMinutes] = useState(20)
  const [error, setError] = useState<string | null>(null)

  const starComplete = STAR_FIELDS.every(
    (field) => star[field.key].trim() !== '',
  )
  const canSubmit =
    starComplete && task.assignmentVersion !== undefined && !mutation.isPending

  const submit = async () => {
    if (!canSubmit) return
    setError(null)
    try {
      await mutation.mutateAsync({
        assignmentId: task.id,
        assignmentVersion: task.assignmentVersion!,
        commandId: crypto.randomUUID(),
        sourceId: crypto.randomUUID(),
        kind: 'behavioral',
        durationSeconds: Math.max(0, Math.round(minutes)) * 60,
        artifact: {
          question: task.title,
          star: {
            situation: star.situation,
            task: star.task,
            action: star.action,
            result: star.result,
          },
          transcriptRef: null,
          recordingRef: recordingRef.trim() === '' ? null : recordingRef.trim(),
        },
        problemSessionId: null,
        interviewSessionId: null,
      })
      await Promise.all([
        utils.dashboard.plan.list.invalidate(),
        utils.dashboard.feedback.list.invalidate(),
      ])
      onOpenChange(false)
    } catch {
      setError('Could not submit for review. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Submit your STAR story</DialogTitle>
          <DialogDescription>
            {task.title} — all four STAR spans are required before your coach
            can review it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {STAR_FIELDS.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <Label htmlFor={`star-${field.key}`}>{field.label}</Label>
              <Textarea
                id={`star-${field.key}`}
                value={star[field.key]}
                onChange={(event) =>
                  setStar((prev) => ({
                    ...prev,
                    [field.key]: event.target.value,
                  }))
                }
                rows={3}
              />
            </div>
          ))}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="star-recording">Recording link (optional)</Label>
            <Input
              id="star-recording"
              value={recordingRef}
              onChange={(event) => setRecordingRef(event.target.value)}
              placeholder="Link to your practice recording"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="star-minutes">Time spent (minutes)</Label>
            <Input
              id="star-minutes"
              type="number"
              min={0}
              value={minutes}
              onChange={(event) => setMinutes(Number(event.target.value))}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit}>
              {mutation.isPending ? 'Submitting…' : 'Submit for review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
